import { header_template, headerStatusTemplate, statusStyle } from "../../admin/constants/constants";
import { text_comparator } from "../../shared/component/grid/comparators";
import { getStateIdFromCode, stateCodeToIdMapping } from "../../shared/shared_constants";

//#region Type Definitions
export enum ChargeByTabId {
    RevenueCenter = 0,
    ProcedureCode = 1
}

export interface ChargeByItem {
    label: string; // Selected item from the dropdown
    tabId: ChargeByTabId; // Tab id from the dropdown (Revenue Center or Procedure Code)
    value: number; // Id from the selected entity (Revenue Center or Procedure code)
}

export interface AppliedModifierFormData {
    setting_name: string;
    active_status: boolean;
    modifier: string;
    to_charge_by: ChargeByItem[];
    start_date: string;
    end_date: string;
    state: any[];
    provider_type: string;
    primary_specialty: any[];
    payer: any[];
    insurance_class: any[];
    excluded_insurance_class: any[];
}


export interface AppliedModifierDropdownData {
    modifier_list: any[];
    to_charge_by_list: any[];
    state_list: any[];
    primary_specialty_list: any[];
}

export interface AppliedModifierPanelData {
    payer_list: any[];
    insurance_class_list: any[];
}

// Default object factories
export const createDefaultFormData = (): AppliedModifierFormData => ({
    setting_name: '',
    active_status: true,
    modifier: '0', // Default to first option
    to_charge_by: [],
    start_date: '',
    end_date: '',
    state: [] as any[],
    provider_type: '0', // Default to "None"
    primary_specialty: [] as any[],
    payer: [] as any[],
    insurance_class: [] as any[],
    excluded_insurance_class: [] as any[]
});


export const createDefaultDropdownData = (): AppliedModifierDropdownData => ({
    modifier_list: [],
    to_charge_by_list: [],
    state_list: [] as any[],
    primary_specialty_list: [] as any[]
});

export const createDefaultPanelData = (): AppliedModifierPanelData => ({
    payer_list: [] as any[],
    insurance_class_list: [] as any[]
});

// Helper function to create a ChargeByItem
export const createChargeByItem = (label: string, tabId: ChargeByTabId, value: number): ChargeByItem => ({
    label,
    tabId,
    value
});

// Data transformation utilities
export const transformBackendDtoToFormData = (backendDto: any): AppliedModifierFormData => {
    // Create a reverse mapping from the shared stateCodeToIdMapping
    // This dynamically creates ID -> Code mapping from the existing Code -> ID mapping
    const stateIdToCodeMapping: { [key: number]: string } = {};
    Object.keys(stateCodeToIdMapping).forEach(function(code) {
        const id = stateCodeToIdMapping[code];
        if (id > 0) {
            stateIdToCodeMapping[id] = code;
        }
    });
    
    // Initialize the form data
    const modifierId = (backendDto.applied_modifier && backendDto.applied_modifier.procedure_modifier_id) 
        ? backendDto.applied_modifier.procedure_modifier_id.toString() 
        : '0';
    
    const formData: AppliedModifierFormData = {
        setting_name: backendDto.claim_processing_rule_name || '',
        active_status: backendDto.is_active || false,
        modifier: modifierId,
        to_charge_by: [],
        start_date: backendDto.start_date || '',
        end_date: backendDto.end_date || '',
        state: [],
        provider_type: '0', // Default to "None"
        primary_specialty: [],
        payer: [],
        insurance_class: [],
        excluded_insurance_class: []
    };
    
    // Determine if insurance classes should be excluded based on criteria type
    // Insurance classes are excluded when criteria is either:
    // - excluded_insurance_classes (3): standalone excluded insurance classes
    // - payer_exclude_insurance_classes (8): payer with excluded insurance classes
    const criteriaId = (backendDto.claim_processing_criteria && backendDto.claim_processing_criteria.claim_processing_criteria_id) 
        ? backendDto.claim_processing_criteria.claim_processing_criteria_id 
        : 0;
    const isExcludedInsuranceClass = criteriaId === criteria_type.excluded_insurance_classes || 
                                     criteriaId === criteria_type.payer_exclude_insurance_classes;
    
    // Parse claim_processing_criteria_source_details
    if (backendDto.claim_processing_criteria_source_details && Array.isArray(backendDto.claim_processing_criteria_source_details)) {
        backendDto.claim_processing_criteria_source_details.forEach(detail => {
            const typeId = detail.claim_processing_criteria_source_type_id;
            const dataId = detail.claim_processing_criteria_source_data_id;
            
            // Revenue centers
            if (typeId === criteria_source_type.revenue_center) {
                formData.to_charge_by.push({
                    label: '', // Will be populated by the component from the dropdown
                    tabId: ChargeByTabId.RevenueCenter,
                    value: dataId
                });
            }
            // Procedure codes
            else if (typeId === criteria_source_type.procedure_code) {
                formData.to_charge_by.push({
                    label: '', // Will be populated by the component from the dropdown
                    tabId: ChargeByTabId.ProcedureCode,
                    value: dataId
                });
            }
            // Locations (states)
            else if (typeId === criteria_source_type.location) {
                const stateCode = stateIdToCodeMapping[dataId];
                if (stateCode) {
                    formData.state.push(stateCode);
                }
            }
            // Provider type
            else if (typeId === criteria_source_type.provider_type) {
                formData.provider_type = dataId.toString();
            }
            // Primary specialty
            else if (typeId === criteria_source_type.primary_specialty) {
                formData.primary_specialty.push(dataId);
            }
            // Payer
            else if (typeId === criteria_source_type.payer) {
                formData.payer.push(dataId);
            }
            // Insurance class (check if excluded or included based on criteria type)
            else if (typeId === criteria_source_type.insurance_class) {
                if (isExcludedInsuranceClass) {
                    formData.excluded_insurance_class.push(dataId);
                } else {
                    formData.insurance_class.push(dataId);
                }
            }
        });
    }
    
    return formData;
};

export const transformFormDataToBackendDto = (formData: AppliedModifierFormData, claimProcessingRuleId: number = 0) => {
    
    // Transform to_charge_by array into criteria source details
    const criteriaSourceDetails = [];
    
    // Add revenue centers and procedure codes from to_charge_by
    if (formData.to_charge_by && formData.to_charge_by.length > 0) {
        formData.to_charge_by.forEach(item => {
            if (item.tabId === ChargeByTabId.RevenueCenter) {
                criteriaSourceDetails.push({
                    claim_processing_criteria_source_type_id: criteria_source_type.revenue_center,
                    claim_processing_criteria_source_data_id: item.value
                });
            } else if (item.tabId === ChargeByTabId.ProcedureCode) {
                criteriaSourceDetails.push({
                    claim_processing_criteria_source_type_id: criteria_source_type.procedure_code,
                    claim_processing_criteria_source_data_id: item.value
                });
            }
        });
    }
    
    // Add states - use shared state mapping
    if (formData.state && formData.state.length > 0) {
        formData.state.forEach(stateCode => {
            const stateId = getStateIdFromCode(stateCode);
            
            if (stateId > 0) {
                criteriaSourceDetails.push({
                    claim_processing_criteria_source_type_id: criteria_source_type.location,
                    claim_processing_criteria_source_data_id: stateId
                });
            }
        });
    }
    
    // Add provider types
    if (formData.provider_type && formData.provider_type !== ProviderTypeValues.NONE) {
        criteriaSourceDetails.push({
            claim_processing_criteria_source_type_id: criteria_source_type.provider_type,
            claim_processing_criteria_source_data_id: parseInt(formData.provider_type)
        });
    }
    
    // Add primary specialties
    if (formData.primary_specialty && formData.primary_specialty.length > 0) {
        formData.primary_specialty.forEach(specialtyId => {
            criteriaSourceDetails.push({
                claim_processing_criteria_source_type_id: criteria_source_type.primary_specialty,
                claim_processing_criteria_source_data_id: parseInt(specialtyId)
            });
        });
    }
    
    // Add payers
    if (formData.payer && formData.payer.length > 0) {
        formData.payer.forEach(payerId => {
            criteriaSourceDetails.push({
                claim_processing_criteria_source_type_id: criteria_source_type.payer,
                claim_processing_criteria_source_data_id: parseInt(payerId)
            });
        });
    }
    
    // Add insurance classes
    if (formData.insurance_class && formData.insurance_class.length > 0) {
        formData.insurance_class.forEach(insuranceClassId => {
            criteriaSourceDetails.push({
                claim_processing_criteria_source_type_id: criteria_source_type.insurance_class,
                claim_processing_criteria_source_data_id: parseInt(insuranceClassId)
            });
        });
    }
    
    // Add excluded insurance classes
    if (formData.excluded_insurance_class && formData.excluded_insurance_class.length > 0) {
        formData.excluded_insurance_class.forEach(excludedInsuranceClassId => {
            criteriaSourceDetails.push({
                claim_processing_criteria_source_type_id: criteria_source_type.insurance_class,
                claim_processing_criteria_source_data_id: parseInt(excludedInsuranceClassId)
            });
        });
    }
    
    // Create the backend DTO structure
    const criteriaTypeId = getCriteriaTypeFromFormData(formData);
    
    const backendDto = {
        claim_processing_rule_id: claimProcessingRuleId,
        claim_processing_rule_name: formData.setting_name,
        applied_modifier: {
            procedure_modifier_id: parseInt(formData.modifier),
            proc_mod_code: '', // This will be populated by the backend
            is_active: formData.active_status
        },
        claim_processing_criteria: {
            claim_processing_criteria_id: 0, // This will be determined by the backend based on the criteria type
            claim_processing_criteria_name: '', // This will be populated by the backend
            claim_processing_criteria_type_id: criteriaTypeId // Determine criteria type based on form data
        },
        claim_processing_criteria_source_details: criteriaSourceDetails,
        is_active: formData.active_status,
        start_date: formData.start_date,
        end_date: formData.end_date || null
    };
    
    return backendDto;
};

// Helper function to determine criteria type from form data
export const getCriteriaTypeFromFormData = (formData: AppliedModifierFormData) => {
    const hasPayer = formData.payer && formData.payer.length > 0;
    const hasInsuranceClass = formData.insurance_class && formData.insurance_class.length > 0;
    const hasExcludedInsuranceClass = formData.excluded_insurance_class && formData.excluded_insurance_class.length > 0;
    
    // Determine criteria type based on the combination of selections
    // 1. Payer + Insurance Class → payer_include_insurance_classes
    if (hasPayer && hasInsuranceClass) {
        return criteria_type.payer_include_insurance_classes;
    }
    // 2. Payer + Excluded Insurance Class → payer_exclude_insurance_classes
    else if (hasPayer && hasExcludedInsuranceClass) {
        return criteria_type.payer_exclude_insurance_classes;
    }
    // 3. Only Payer → payers
    else if (hasPayer) {
        return criteria_type.payers;
    }
    // 4. Only Insurance Class → insurance_classes
    else if (hasInsuranceClass) {
        return criteria_type.insurance_classes;
    }
    // 5. Only Excluded Insurance Class → excluded_insurance_classes
    else if (hasExcludedInsuranceClass) {
        return criteria_type.excluded_insurance_classes;
    }
    
    return criteria_type.payers; // Default
};

//#endregion

//#region UI Elements Name and ID definition
export const new_button = {
    name: 'New',
    id: 'applied_modifiers_new_button_id'
}

export const save_button = {
    name: 'Save',
    id: 'applied_modifiers_save_button_id'
}

export const export_button = {
    name: 'Export',
    id: 'applied_modifiers_export_button_id'
}

export const print_button = {
    name: 'Print',
    id: 'applied_modifiers_print_button_id'
}
//#endregion

export const page_title = {
    name: 'Applied Modifiers',
    id: 'applied_modifiers_header_id'
}

export const grid_settings_criteria_column =
{
    grid_id: 'grid_applied_modifiers',
    no_records_found_message: 'No Record Available',
    column_defs:
        [
            {
                headerName: 'Setting Name',
                headerComponentParams: header_template,
                field: 'claim_processing_rule_name',
                comparator: text_comparator,
                sortable: true,
                filter: 'textFilter',
                type: 'string',
                unSortIcon: true,
                resizable: false,
                suppressSizeToFit: true,
            },
            {
                headerName: 'Modifier',
                headerComponentParams: header_template,
                field: 'modifier',
                comparator: text_comparator,
                sortable: true,
                filter: 'textFilter',
                type: 'string',
                unSortIcon: true,
            },
            {
                headerName: 'Procedure Code(s)',
                headerComponentParams: header_template,
                field: 'qualifying_criteria_procedure_codes',
                comparator: text_comparator,
                sortable: true,
                filter: 'textFilter',
                type: 'string',
                unSortIcon: true,
                suppressSizeToFit: true,
                cellRenderer: 'GridCellButtonTooltipWithContent'
            },
            {
                headerName: 'Revenue Center',
                headerComponentParams: header_template,
                field: 'qualifying_criteria_revenue_center',
                comparator: text_comparator,
                sortable: true,
                filter: 'textFilter',
                type: 'string',
                unSortIcon: true,
                suppressSizeToFit: true,
                cellRenderer: 'GridCellButtonTooltipWithContent'
            },
            {
                headerName: 'Effective From',
                headerComponentParams: header_template,
                field: 'start_date',
                filter: 'textFilter',
                type: 'string',
                unSortIcon: true,
            },
            {
                headerName: 'Effective Through',
                headerComponentParams: header_template,
                field: 'end_date',
                filter: 'textFilter',
                type: 'string',
                unSortIcon: true,
            },
            {
                headerName: 'Payer',
                headerComponentParams: header_template,
                field: 'qualifying_criteria_payer',
                comparator: text_comparator,
                sortable: true,
                filter: 'textFilter',
                type: 'string',
                unSortIcon: true,
                suppressSizeToFit: true,
                cellRenderer: 'GridCellButtonTooltipWithContent'
            },
            {
                headerName: 'Insurance Class',
                headerComponentParams: header_template,
                field: 'qualifying_criteria_insurance_class',
                comparator: text_comparator,
                sortable: true,
                filter: 'textFilter',
                type: 'string',
                unSortIcon: true,
                suppressSizeToFit: true,
                cellRenderer: 'GridCellButtonTooltipWithContent'
            },
            {
                headerName: 'Location',
                headerComponentParams: header_template,
                field: 'qualifying_criteria_location',
                comparator: text_comparator,
                sortable: true,
                filter: 'textFilter',
                type: 'string',
                unSortIcon: true,
                suppressSizeToFit: true,
                cellRenderer: 'GridCellButtonTooltipWithContent'
            },
            {
                headerName: 'Provider Type',
                headerComponentParams: header_template,
                field: 'provider_type',
                comparator: text_comparator,
                sortable: true,
                filter: 'textFilter',
                type: 'string',
                unSortIcon: true,
            },
            {
                headerName: 'Primary Specialty',
                headerComponentParams: header_template,
                field: 'qualifying_criteria_primary_specialty',
                comparator: text_comparator,
                sortable: true,
                filter: 'textFilter',
                type: 'string',
                unSortIcon: true,
                suppressSizeToFit: true,
                cellRenderer: 'GridCellButtonTooltipWithContent',
            },
            {
                headerName: 'Last Update User',
                headerComponentParams: header_template,
                comparator: text_comparator,
                sortable: true,
                field: 'last_updated_user',
                filter: 'textFilter',
                type: 'string',
                unSortIcon: true,

            },
            {
                headerName: 'Last Update Date/Time',
                headerComponentParams: header_template,
                field: 'last_updated_date_time',
                filter: 'textFilter',
                type: 'string',
                unSortIcon: true,
            },
            {
                headerName: 'Status',
                headerComponentParams: headerStatusTemplate,
                comparator: text_comparator,
                sortable: true,
                field: 'status',
                filter: 'checklistFilter',
                minWidth: 120,
                width: 120,
                type: 'string',
                cellStyle: statusStyle,
                unSortIcon: true
            }
        ]
};

export const loading_identifier = {
    applied_modifiers_list: 'applied_modifiers_list',
    criteria: 'criteria',
    insurance_class: 'insurance_class',
    payer: 'payer',
    initial_api_data: 'initial_api_data'
}

export const filter_options = {
    show_inactive: {
        type: 'show_inactive',
        settings: {
            name: 'Show Inactive',
            id: 'applied_modifiers_show_inactive_id'
        }
    },
    show_expired: {
        type: 'show_expired',
        settings: {
            name: 'Show Expired',
            id: 'applied_modifiers_show_expired_id'
        }
    },
    show_future: {
        type: 'show_future',
        settings: {
            name: 'Show Future',
            id: 'applied_modifiers_show_future_id'
        }
    }
}

export const criteria_type = {
    payers: 1,
    insurance_classes: 2,
    excluded_insurance_classes: 3,
    location_insurance_class: 4,
    location_insurance_code: 5,
    location: 6,
    payer_include_insurance_classes: 7,
    payer_exclude_insurance_classes: 8,
    provider: 9,
    provider_insurance_class: 10,
    provider_insurance_code: 11
}

export const criteria_source_type = {
    payer: 1,
    insurance_class: 2,
    insurance_code: 3,
    place_of_service: 4,
    provider: 5,
    procedure_code: 6,
    applied_modifier: 7,
    location: 8,
    provider_type: 9,
    primary_specialty: 12,
    revenue_center: 11
}

export const statusSelectOptions = [
    {
        value: true,
        text: 'Active'
    },
    {
        value: false,
        text: 'Inactive'
    }
];

export const messages = {
    save_error_msg: 'Error saving Applied Modifier.',
    save_success_msg: 'Applied Modifier successfully saved.'
}

export const prompt_settings = {
    message: 'This action will cause any unsaved data to be lost. Continue?',
    unsaved_changes: 'Unsaved changes',
    continue: 'Continue'
}

export const field_identifier = {
    setting_name: 'applied_modifiers_setting_name',
    active_status: 'applied_modifiers_active_status',
    modifier: 'applied_modifiers_modifier',
    to_charge_by: 'applied_modifiers_to_charge_by',
    date_of_service_range: 'applied_modifiers_date_of_service_range',
    state: 'applied_modifiers_state',
    provider_type: 'applied_modifiers_provider_type',
    primary_specialty: 'applied_modifiers_primary_specialty',
    tabs_wrapper: 'applied_modifiers_tabs_wrapper',
    payer: 'applied_modifiers_payer',
    insurance_class: 'applied_modifiers_insurance_class',
    excluded_insurance_class: 'applied_modifiers_excluded_insurance_class',
    payer_panel: 'applied_modifiers_payer_panel',
    insurance_class_panel: 'applied_modifiers_insurance_class_panel',
    excluded_insurance_class_panel: 'applied_modifiers_excluded_insurance_class_panel'
}

// Base required fields that are always required
export const BASE_REQUIRED_FIELDS = [
    field_identifier.setting_name,
    field_identifier.active_status,
    field_identifier.modifier,
    field_identifier.to_charge_by,
    field_identifier.date_of_service_range,
    field_identifier.tabs_wrapper
];

export const active_status_list = [
    {
        value: true,
        text: 'Active'
    },
    {
        value: false,
        text: 'Inactive'
    }
];

export const revenue_centers_and_proc_codes_tab = {
    tab1: {
        label: 'Revenue Center',
        tab: 1
    },
    tab2: {
        label: 'Procedure Code',
        tab: 2
    }
}

// Provider Type Values
export const ProviderTypeValues = {
    NONE: '0',
    BILLING_PROVIDER: '1',
    TREATING_PROVIDER: '2'
};

export const provider_type_list = [
    {
        value: ProviderTypeValues.NONE,
        text: 'None'
    },
    {
        value: ProviderTypeValues.BILLING_PROVIDER,
        text: 'Billing Provider'
    },
    {
        value: ProviderTypeValues.TREATING_PROVIDER,
        text: 'Treating Provider'
    }
];

//#region UI Constants
/**
 * UI Constants to replace magic numbers and strings throughout the component
 */
export const UI_CONSTANTS = {
    // Default dropdown values
    DEFAULT_VALUES: {
        DROPDOWN_PLACEHOLDER_TEXT: '-- Select --',
        DROPDOWN_DEFAULT_VALUE: '0',
        DROPDOWN_PLACEHOLDER_KEY: '-- Select --',
        DROPDOWN_DEFAULT_CODE: '0'
    },
    
    // Date formats
    DATE_FORMATS: {
        DISPLAY_FORMAT: 'mm/dd/yyyy',
        API_FORMAT: 'YYYY-MM-DDT00:00:00'
    },
    
    // Magic strings for data processing
    DATA_PROCESSING: {
        INACTIVE_SUFFIX: '- INACTIVE',
        EMPTY_STRING: '',
        SPACE_SEPARATOR: ' - '
    },
    
    // CSS class names
    CSS_CLASSES: {
        REQUIRED_ALERT: 'req-alert',
        REQUIRED_ALERT_NORMAL: 'req-alert_normal',
        REQUIRED_BORDER_INPUT: 'req-border-inp',
        REQUIRED_BACKGROUND_INPUT: 'req-background-inp',
        PRIMARY_BUTTON: 'primary',
        APPLIED_MODIFIERS_UI_SECTION_LABEL: 'applied_modifiers_ui_section_label',
        MANAGE_CONTENT_MULTI_SELECT: 'manage-content-multi-select',
        CRITERIA_PANEL: 'criteria_panel',
        APPLIED_MODIFIERS_TABS_ROW: 'applied_modifiers_tabs_row',
        APPLIED_MODIFIERS_TABS_SEGMENT: 'applied_modifiers_tabs_segment',
        APPLIED_MODIFIERS_TABS_COLUMN: 'applied_modifiers_tabs_column',
        LAST_ROW: 'last-row',
        BP3_DIALOG_SAVE_BUTTON: 'bp3-dialog-save-button',
        BP3_FILL: 'bp3-fill'
    },
    
    // UI Labels and Text
    LABELS: {
        SETTING_NAME: 'Setting Name',
        STATUS: 'Status',
        APPLY_SECTION: 'APPLY',
        MODIFIER: 'Modifier',
        TO_CHARGE_BY: 'To Charge by',
        WHEN_SECTION: 'WHEN',
        DATE_OF_SERVICE_RANGE: 'Date of Service Range',
        STATE: 'State',
        PROVIDER_TYPE: 'Provider Type',
        PRIMARY_SPECIALTY: 'Primary Specialty',
        PAYER_TAB: 'Payer',
        INSURANCE_CLASS_TAB: 'Insurance Class',
        EXCLUDED_INSURANCE_CLASS_TAB: 'Excluded Insurance Class',
        REQUIRED_INDICATOR: ' (required)',
        LOADING_TEXT: 'Loading'
    }
};
//#endregion